import * as React from 'react';
import { Helmet } from 'react-helmet';
import { NumberParam, useQueryParam, withDefault } from 'use-query-params';
import { PageSection, Title, Tabs, Tab, TabTitleText, TabTitleIcon } from '@patternfly/react-core';
import { KeyIcon, UsersIcon, UserIcon, WrenchIcon, OptimizeIcon, LockIcon, TagIcon } from '@patternfly/react-icons';
import UserSession from '@app/common/user-session';
import Settings from './Settings';
import Users from './Users';
import ApiKeys from './ApiKeys';
import { Preferences } from './preferences';
import PartnerInfo from './PartnerInfo';
import ResetPassword from '../auth/ResetPassword';
import { useHistory, useLocation } from 'react-router-dom';
import Page from '@app/components/Page';
import loaderLogo from '@app/assets/images/logo/CloudAccess-Logo-BlkCloud.jpg';
import { Button, CardHeader, Col, Row, Card, CardBody } from 'reactstrap';
import ThemeContext from '@app/providers/contexts/ThemeContext';
import { MdAccountCircle, MdPeople, MdSwitchAccount } from 'react-icons/md';
import Loader from '@app/components/Loader';

enum TabIndex {
  PROFILE,
  ACCOUNT,
  TEAMS,
  USERS,
  SECURITY,
  PAYMENT_METHODS,
  API_KEYS
}

interface ThemeContextType {
  theme: string;
  setTheme: (theme: string) => void;
}

export const AdminView: React.FC = () => {
  const TitleMap = {
    [TabIndex.ACCOUNT]: 'Settings',
    [TabIndex.TEAMS]: 'Users'
  };
  const location = useLocation();
  const { theme } = React.useContext(ThemeContext) as ThemeContextType;

  const isImpersonating = UserSession.getParam('isImpersonating');
  const role = UserSession.getParam('role').toLowerCase();
  const isManager = role === 'manager' ? true : false;
  const isViewer = role === 'viewer' ? true : false;
  const isEditor = role === 'editor' ? true : false;
  const defaultTab = isImpersonating ? TabIndex.ACCOUNT : TabIndex.TEAMS;
  const queryParams = new URLSearchParams(location.search);
  const initialTab = Number(queryParams.get('tab')) || defaultTab;
  const [activeTabIndex, setActiveTabIndex] = React.useState(initialTab);
  const [currentTitle, setCurrentTitle] = React.useState('');
  const [isLoading, setIsLoading] = React.useState(true);

  // Create refs for child components
  const settingsRef = React.useRef<{ handleSubmit: () => Promise<void>; isDirty: () => boolean } | null>(null);
  const preferencesRef = React.useRef<{ handleSubmit: () => void; isDirty: () => boolean }>(null);
  const partnerInfoRef = React.useRef<{ handleSubmit: () => void; isDirty: () => boolean }>(null);

  React.useEffect(() => {
    const randomTimeout = Math.floor(Math.random() * (3000 - 1000 + 1)) + 1000;

    const loaderTimeout = setTimeout(() => {
      setIsLoading(false);
    }, randomTimeout);

    return () => clearTimeout(loaderTimeout);
  }, []);

  const handleTabClick = React.useCallback(
    (_, index) => {
      setActiveTabIndex(index);
    },
    [setActiveTabIndex]
  );

  React.useEffect(() => {
    setCurrentTitle(TitleMap[activeTabIndex]);
  }, [activeTabIndex]);

  const handleUnifiedSave = async () => {
    try {
      // Only call save functions if there are changes
      if (settingsRef.current?.handleSubmit && settingsRef.current?.isDirty()) {
        await settingsRef.current.handleSubmit();
      }
      
      // For Preferences, only save if there are actual changes
      if (preferencesRef.current?.handleSubmit && preferencesRef.current?.isDirty()) {
        await preferencesRef.current.handleSubmit();
      }

      // For Partner Info, only save if there are actual changes
      if (partnerInfoRef.current?.handleSubmit && partnerInfoRef.current?.isDirty()) {
        await partnerInfoRef.current.handleSubmit();
      }
    } catch (error) {
      console.error('Error saving:', error);
    }
  };

  return (
    <React.Fragment>
      <Helmet>
        <title>Account Settings</title>
      </Helmet>
      {isLoading ? (
        <Loader />
      ) : (
        <Page
          tag="div"
          className={`cr-page px-3 pt-2 acl-form position-relative ${theme}`}
          title=""
          actionButton={null}
          breadcrumbs={[
            { name: "Account Settings", active: false, url: "/account-settings" },
          ]}
        >
          <Row>
            <Col>
              <div className="p-3 wans-card-wrapper d-flex justify-content-between">
                <Tabs activeKey={activeTabIndex} onSelect={handleTabClick} isBox className="w-100 account-settings-tabs">
                  {!isImpersonating && !isViewer && (
                    <Tab
                      eventKey={TabIndex.TEAMS}
                      title={
                        <React.Fragment>
                          <TabTitleIcon>
                            <MdPeople color="#85ba3b" />
                          </TabTitleIcon>{' '}
                          <TabTitleText>{TitleMap[TabIndex.TEAMS]}</TabTitleText>
                        </React.Fragment>
                      }
                    >
                      <Row>
                        <Users partnerId={''} partnerName={''} />
                      </Row>
                    </Tab>
                  )}
                  <Tab
                    eventKey={TabIndex.ACCOUNT}
                    title={
                      <React.Fragment>
                        <TabTitleIcon>
                          <MdSwitchAccount color="#85ba3b" />
                        </TabTitleIcon>{' '}
                        <TabTitleText>{TitleMap[TabIndex.ACCOUNT]}</TabTitleText>
                      </React.Fragment>
                    }
                  >
                    <Row className="w-100 d-flex flex-nowrap account-settings-row" style={{ marginTop: 'calc(1rem - 14px)', alignItems: 'stretch', gap: '16px' }}>
                      <PartnerInfo ref={partnerInfoRef} />
                      <Preferences ref={preferencesRef} />
                    </Row>
                    <Row className="mx-0 px-0">
                      <Settings ref={settingsRef} />
                      {/* <PaymentMethods /> */}
                    </Row>
                    <Row className="w-100 mt-3">
                      <Col className="d-flex justify-content-center">
                        <Button 
                          className="ethica-button-green"
                          onClick={handleUnifiedSave}
                        >
                          Save
                        </Button>
                      </Col>
                    </Row>
                  </Tab>
                </Tabs>
              </div>
            </Col>
          </Row>
        </Page>
      )}
    </React.Fragment>
  );
};
